// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  OnDestroy,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { CtsGantryStartupEventFilterComponent } from '../cts-gantry-startup-event-filter/cts-gantry-startup-event-filter.component';
import { CtsGantryInitEvent } from '../../interfaces/cts-gantry-init-event';
import { CtsGantryLog } from '../../interfaces/cts-gantry-log';
import { CtsGantryFilterLogService } from '../../services/cts-gantry-filter-log.service';

import { CtstatusService } from '../../services/ctstatus.service';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { SystemOverviewService } from 'src/app/modules/portal/services/systemOverview.service';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { CtsGantryScrollPaginationModel } from '../../model/cts-gantry-scroll-pagination-model';
import { CtsGantryLogEmptyStateComponent } from '../cts-gantry-log-empty-state/cts-gantry-log-empty-state.component';
import { CtsGantryLogData } from '../../interfaces/cts-gantry-log-data';
import { CtsGantryCacheModel } from '../../model/cts-gantry-cache-model';
import { CtsGantryTimezonePipe } from '../../pipe/cts-gantry-timezone.pipe';
import { CtsGantryTimezoneSelectComponent } from '../cts-gantry-timezone-select/cts-gantry-timezone-select.component';
import { CtsGantryLogSeverityType } from '../../enum/cts-gantry-log-severity-type';
import { CtsGantryFilterDropdownComponent } from '../../cts-gantry-filter-dropdown/cts-gantry-filter-dropdown.component';

@Component({
  standalone: true,
  imports: [
    TranslateModule,
    CtsGantryStartupEventFilterComponent,
    CtsGantryLogEmptyStateComponent,
    RouterModule,
    APICallGateComponent,
    CtsGantryTimezonePipe,
    CtsGantryTimezoneSelectComponent,
    CommonModule,
    CtsGantryFilterDropdownComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [CtsGantryFilterLogService],
  selector: 'app-cts-gantry-log-detail',
  templateUrl: './cts-gantry-log-detail.component.html',
  styleUrl: './cts-gantry-log-detail.component.scss',
})
export class CtsGantryLogDetailComponent implements OnDestroy {
  @ViewChild('searchInput') searchInput: ElementRef | undefined;

  public systemMaterialNumber: number | undefined;
  public systemSerialNumber: string | undefined;

  events: CtsGantryInitEvent[] = [];
  logs: CtsGantryLog[] = [];
  filteredLogs: CtsGantryLog[] = [];

  protected readonly STATUS_ERROR = CtsGantryLogSeverityType.ERROR;
  private readonly unsubscribe = new Subject<void>();
  protected selectedEvent: CtsGantryInitEvent | undefined;
  protected softwareVersion: string | undefined;

  protected gantryEventLogs = this.ctsystemService.gantryEventLogs;
  protected gantryEvents = this.ctsystemService.gantryEvents;

  protected readonly pagination = new CtsGantryScrollPaginationModel();
  protected readonly systemDetails = this.systemOverviewService.systemDetails;

  public systemDetails$ = this.systemDetails.lastSuccessfulData$;
  private readonly logCacheModel = new CtsGantryCacheModel<CtsGantryLogData>();

  public constructor(
    protected readonly systemOverviewService: SystemOverviewService,
    private readonly activatedRoute: ActivatedRoute,
    protected readonly filterLogService: CtsGantryFilterLogService,
    protected readonly ctsystemService: CtstatusService,
    private readonly renderer: Renderer2,
  ) {
    this.filterLogService.initLogFilterService(
      this.logs,
      this.updateFilteredLogs,
    );
    this.listenToSystemDetails();
    this.handleSuccessfulData();
  }

  public ngOnDestroy(): void {
    this.filterLogService.unsubscribeSubjects();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private listenToSystemDetails(): void {
    this.systemDetails$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.systemDetailsSubscribeFn);
  }

  private readonly systemDetailsSubscribeFn = (details: any) => {
    if (details) {
      this.systemMaterialNumber = details.systemMaterialNumber;
      this.systemSerialNumber = details.systemSerialNumber;
      this.updateSoftwareVersion(details);
      this.requestEvents();
    }
  };

  private initSelectedEvent(): void {
    const eventSource =
      this.activatedRoute.snapshot.paramMap.get('eventSource');
    this.selectedEvent = this.events?.find(
      (ctsEvent) => ctsEvent.eventSource === eventSource,
    );

    if (this.selectedEvent) {
      if (this.selectedEvent.softwareVersion !== null) {
        this.softwareVersion = this.selectedEvent?.softwareVersion;
      }
      this.requestEventLogs(this.selectedEvent.eventSource);
    }
  }

  private readonly updateSoftwareVersion = (systemDetails: any) => {
    this.softwareVersion = systemDetails?.systemSoftwareVersion.split('-')[0];
  };

  private requestEventLogs(eventSource: string) {
    if (!this.logCacheModel.hasData(eventSource)) {
      this.gantryEventLogs.request({
        EventSource: eventSource,
        MaterialNumber: this.systemMaterialNumber!,
        SerialNumber: this.systemSerialNumber!,
      });
    } else {
      this.getFromCache(eventSource);
    }
  }

  private getFromCache(eventSource: string): void {
    const logData = this.logCacheModel.getCachedData(eventSource);
    this.initLogAndFilter(logData!);
  }

  private readonly logSubscribeFn = (res: any) => {
    this.logCacheModel.cacheData(res.eventSource, res);
    this.initLogAndFilter(res);
  };

  private readonly initLogAndFilter = (res: any) => {
    if (res?.logs) {
      this.logs = res.logs as CtsGantryLog[];
    } else {
      this.logs = [];
    }

    this.filterLogService.initLogFilterService(
      this.logs,
      this.updateFilteredLogs,
    );
  };

  private requestEvents() {
    this.gantryEvents.request({
      MaterialNumber: this.systemMaterialNumber!,
      SerialNumber: this.systemSerialNumber!,
    });
  }

  private readonly requestEventsSubscribeFn = (res: any) => {
    if (res?.events) {
      this.events = res?.events as unknown as CtsGantryInitEvent[];
    } else {
      this.events = [];
    }
    this.initSelectedEvent();
  };

  public handleSuccessfulData(): void {
    this.gantryEventLogs.lastSuccessfulData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.logSubscribeFn);

    this.gantryEvents.lastSuccessfulData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.requestEventsSubscribeFn);
  }

  protected onSelectedEventChange(ctsEvent: CtsGantryInitEvent): void {
    this.clearSearchInput();
    this.requestEventLogs(ctsEvent.eventSource);
  }

  private clearSearchInput() {
    if (this.searchInput) {
      this.renderer.setProperty(this.searchInput.nativeElement, 'value', '');
    }
  }

  private readonly updateFilteredLogs = (logs: CtsGantryLog[]) => {
    this.pagination.totalElements = logs.length;
    this.pagination.pageIndex = 0;
    this.pagination.updatePageFn = (start: number, end: number) => {
      this.filteredLogs.push(...logs.slice(start, end));
    };

    this.filteredLogs = logs.slice(0, this.pagination.pageSize);
  };
}
